import { z } from 'zod';
import { CanaleAcquisizione, StatoCliente } from './enums';
import { fileSlotSchema } from '../file';
import {
  auditFieldsSchema,
  codiceAtecoSchema,
  codiceFiscaleSchema,
  codiceSdiSchema,
  dataOraSchema,
  emailSchema,
  idSchema,
  idSelezioneSchema,
  indirizzoSchema,
  paginationQuerySchema,
  partitaIvaSchema,
  telefonoSchema,
} from '../common';

const clienteBaseSchema = z.object({
  ragioneSociale: z.string().trim().min(1).max(255),
  nomeCommerciale: z.string().trim().max(255).optional(),
  /** Opzionale: un professionista può non averla. */
  partitaIva: partitaIvaSchema.optional(),
  /** Chiave logica univoca e immutabile. */
  codiceFiscale: codiceFiscaleSchema,
  codiceAteco: codiceAtecoSchema.optional(),
  tipologiaId: idSelezioneSchema('Seleziona la forma giuridica'),
  stato: StatoCliente.default('Prospect'),
  canaleAcquisizione: CanaleAcquisizione,
  /** Obbligatorio se il canale è Esterno — vedi refine sotto. */
  partnerPersonaSegnalatoreId: idSelezioneSchema('Seleziona la persona segnalatrice').optional(),
  sedeLegale: indirizzoSchema,
  email: emailSchema.optional(),
  pec: emailSchema.optional(),
  telefono: telefonoSchema.optional(),
  codiceSdi: codiceSdiSchema.optional(),
});

/**
 * Regola condizionale del: il segnalatore è obbligatorio se il canale è
 * "Esterno", e non ha senso se è "Diretto". Vive UNA VOLTA SOLA qui e viene
 * applicata identica dall'API (validazione richiesta) e dal form Vue.
 */
export const clienteCreateSchema = clienteBaseSchema
  .extend({
    /**
     * Copia la sede legale fra le sedi operative già alla creazione, così la
     * si può scegliere come sede di un progetto. Non è un campo dell'anagrafica:
     * produce una riga di sede, che poi vive di vita propria.
     */
    sedeLegaleTraOperative: z.boolean().default(false),
  })
  .superRefine((c, ctx) => {
    if (c.canaleAcquisizione === 'Esterno' && c.partnerPersonaSegnalatoreId == null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Il segnalatore è obbligatorio se il canale di acquisizione è Esterno',
        path: ['partnerPersonaSegnalatoreId'],
      });
    }
    if (c.canaleAcquisizione === 'Diretto' && c.partnerPersonaSegnalatoreId != null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Il segnalatore va indicato solo per i clienti con canale Esterno',
        path: ['partnerPersonaSegnalatoreId'],
      });
    }
  });
export type ClienteCreate = z.infer<typeof clienteCreateSchema>;

/** In update i campi sono opzionali, ma il codice fiscale resta immutabile. */
export const clienteUpdateSchema = clienteBaseSchema.omit({ codiceFiscale: true }).partial();
export type ClienteUpdate = z.infer<typeof clienteUpdateSchema>;

export const clienteSchema = clienteBaseSchema.extend({
  id: idSchema,
  /** Nome della forma giuridica, per mostrarla senza rileggere la lista. */
  tipologia: z.string(),
  creatoDa: idSchema.nullable(),
  creatoIl: dataOraSchema,
});
export type Cliente = z.infer<typeof clienteSchema>;

/* ── Vista di lista ───────────────────────────────────────────────────────── */

/**
 * Riga della tabella clienti: i pochi campi che servono a scansionare l'elenco.
 * Il comune arriva dalla sede legale, i referenti sono un semplice conteggio.
 */
export const clienteListItemSchema = z.object({
  id: idSchema,
  ragioneSociale: z.string(),
  nomeCommerciale: z.string().nullable(),
  codiceFiscale: z.string(),
  partitaIva: z.string().nullable(),
  tipologia: z.string(),
  stato: StatoCliente,
  canaleAcquisizione: CanaleAcquisizione,
  comune: z.string().nullable(),
  provincia: z.string().nullable(),
  nReferenti: z.number().int(),
  creatoIl: dataOraSchema,
});
export type ClienteListItem = z.infer<typeof clienteListItemSchema>;

/* ── Entità collegate ─────────────────────────────────────────────────────── */

export const clienteSedeOperativaCreateSchema = z.object({
  etichetta: z.string().trim().min(1).max(120),
  indirizzo: indirizzoSchema,
  telefono: telefonoSchema.optional(),
  email: emailSchema.optional(),
});
export type ClienteSedeOperativaCreate = z.infer<typeof clienteSedeOperativaCreateSchema>;

export const clienteSedeOperativaUpdateSchema = clienteSedeOperativaCreateSchema.partial();
export type ClienteSedeOperativaUpdate = z.infer<typeof clienteSedeOperativaUpdateSchema>;

export const clienteSedeOperativaSchema = clienteSedeOperativaCreateSchema.extend({
  id: idSchema,
  clienteId: idSchema,
  /** Copia della sede legale, al più una per cliente: non la segue se cambia. */
  daSedeLegale: z.boolean(),
  ...auditFieldsSchema.shape,
});
export type ClienteSedeOperativa = z.infer<typeof clienteSedeOperativaSchema>;

export const clientePersonaCreateSchema = z.object({
  nome: z.string().trim().min(1).max(80),
  cognome: z.string().trim().min(1).max(80),
  ruolo: z.string().trim().max(120).optional(),
  email: emailSchema.optional(),
  telefono: telefonoSchema.optional(),
});
export type ClientePersonaCreate = z.infer<typeof clientePersonaCreateSchema>;

export const clientePersonaUpdateSchema = clientePersonaCreateSchema.partial();
export type ClientePersonaUpdate = z.infer<typeof clientePersonaUpdateSchema>;

export const clientePersonaSchema = clientePersonaCreateSchema.extend({
  id: idSchema,
  clienteId: idSchema,
  ...auditFieldsSchema.shape,
});
export type ClientePersona = z.infer<typeof clientePersonaSchema>;

export const clienteNotaCreateSchema = z.object({
  testo: z.string().trim().min(1).max(10_000),
});
export type ClienteNotaCreate = z.infer<typeof clienteNotaCreateSchema>;

export const clienteNotaSchema = clienteNotaCreateSchema.extend({
  id: idSchema,
  clienteId: idSchema,
  /** Nome dell'autore per il diario, risolto dal join (null se utente rimosso). */
  autore: z.string().nullable(),
  creatoDa: idSchema.nullable(),
  creatoIl: dataOraSchema,
});
export type ClienteNota = z.infer<typeof clienteNotaSchema>;

/**
 * Documento cliente. Il tipo viene dalla lookup `tipo_documento_cliente`; con
 * "Altro" l'etichetta libera diventa obbligatoria.
 */
export const clienteDocumentoCreateSchema = z
  .object({
    tipoDocumentoId: idSelezioneSchema('Seleziona il tipo di documento'),
    etichetta: z.string().trim().max(200).optional(),
    fileSlotId: idSchema.optional(),
  })
  .refine((d) => d.etichetta == null || d.etichetta.length > 0, {
    message: "L'etichetta non può essere vuota",
    path: ['etichetta'],
  });
export type ClienteDocumentoCreate = z.infer<typeof clienteDocumentoCreateSchema>;

/**
 * Vista di un documento cliente: la classificazione (tipo/etichetta) più lo
 * stato del suo slot (presente/vuoto), così la UI non deve fare una seconda
 * chiamata solo per sapere se il file è stato caricato.
 */
export const clienteDocumentoSchema = z.object({
  id: idSchema,
  clienteId: idSchema,
  tipoDocumentoId: idSchema,
  etichetta: z.string().nullable(),
  slot: fileSlotSchema,
});
export type ClienteDocumento = z.infer<typeof clienteDocumentoSchema>;

/**
 * Una riga della lookup `tipo_documento_cliente` (relazione, editabile),
 * per il picker "Aggiungi documento". Nome distinto dall'enum `TipoDocumentoCliente`
 * di `enums.ts` (i valori di seed iniziale): qui è la riga di DB con il suo id.
 */
export const tipoDocumentoClienteRigaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  /** "Altro" richiede l'etichetta libera sul documento. */
  richiedeEtichetta: z.boolean(),
  /**
   * Documento atteso PRIMA di aprire una pratica al cliente. Diverso dagli slot
   * obbligatori di progetto e fase, che dicono cosa serve dentro una pratica.
   * Non blocca nulla: segnala.
   */
  richiestoPerProgetto: z.boolean(),
});
export type TipoDocumentoClienteRiga = z.infer<typeof tipoDocumentoClienteRigaSchema>;

/**
 * Vista amministrativa di una riga della lookup (gestione della lista, non il
 * picker): in più rispetto a `tipoDocumentoClienteRigaSchema` porta l'ordine
 * di visualizzazione e lo stato attivo/disattivato. Nome distinto da
 * `TipoDocumentoCliente` di `enums.ts` (i valori di seed iniziale).
 */
export const tipoDocumentoClienteVistaSchema = tipoDocumentoClienteRigaSchema.extend({
  ordine: z.number().int(),
  attivo: z.boolean(),
});
export type TipoDocumentoClienteVista = z.infer<typeof tipoDocumentoClienteVistaSchema>;

export const tipoDocumentoClienteCreateSchema = z.object({
  nome: z.string().trim().min(1).max(120),
  richiedeEtichetta: z.boolean().default(false),
  richiestoPerProgetto: z.boolean().default(false),
});
export type TipoDocumentoClienteCreate = z.infer<typeof tipoDocumentoClienteCreateSchema>;

export const tipoDocumentoClienteUpdateSchema = tipoDocumentoClienteCreateSchema.partial();
export type TipoDocumentoClienteUpdate = z.infer<typeof tipoDocumentoClienteUpdateSchema>;

/** Nuovo ordine di visualizzazione: l'elenco completo degli id, nell'ordine voluto. */
export const tipoDocumentoClienteRiordinaSchema = z.object({
  ids: z.array(idSchema).min(1),
});
export type TipoDocumentoClienteRiordina = z.infer<typeof tipoDocumentoClienteRiordinaSchema>;

/**
 * Una riga della lookup `tipologia_cliente`. Il picker riceve anche le
 * disattivate: nasconde quelle, ma deve saper mostrare la forma giuridica di
 * un cliente che ne ha una disattivata.
 */
export const tipologiaClienteVistaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  ordine: z.number().int(),
  attivo: z.boolean(),
});
export type TipologiaClienteVista = z.infer<typeof tipologiaClienteVistaSchema>;

export const tipologiaClienteCreateSchema = z.object({
  nome: z.string().trim().min(1).max(80),
});
export type TipologiaClienteCreate = z.infer<typeof tipologiaClienteCreateSchema>;

export const tipologiaClienteUpdateSchema = tipologiaClienteCreateSchema.partial();
export type TipologiaClienteUpdate = z.infer<typeof tipologiaClienteUpdateSchema>;

/** Nuovo ordine di visualizzazione: l'elenco completo degli id, nell'ordine voluto. */
export const tipologiaClienteRiordinaSchema = z.object({
  ids: z.array(idSchema).min(1),
});
export type TipologiaClienteRiordina = z.infer<typeof tipologiaClienteRiordinaSchema>;

/** Referenti interni: legame molti-a-molti cliente ↔︎ utente. */
export const clienteReferentiSetSchema = z.object({
  utenteIds: z.array(idSchema),
});
export type ClienteReferentiSet = z.infer<typeof clienteReferentiSetSchema>;

/** Vista compatta di un referente (per le card della scheda cliente). */
export const clienteReferenteSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  ruolo: z.string(),
});
export type ClienteReferente = z.infer<typeof clienteReferenteSchema>;

export const clienteListQuerySchema = paginationQuerySchema.extend({
  stato: StatoCliente.optional(),
  tipologiaId: idSchema.optional(),
  canaleAcquisizione: CanaleAcquisizione.optional(),
  referenteId: idSchema.optional(),
});
export type ClienteListQuery = z.infer<typeof clienteListQuerySchema>;
